/**
 * SMS Utility Functions - Twilio GSM-7 Compliant
 * 
 * GSM-7 encoding rules:
 * - Single SMS: 160 characters
 * - Multi-SMS: 153 characters per segment (7 chars used for concatenation header)
 * 
 * Unicode/Emoji rules:
 * - Single SMS: 70 characters
 * - Multi-SMS: 67 characters per segment
 */

// GSM-7 basic character set (including extended characters that count as 2)
const GSM_7_BASIC_CHARS = new Set([
  '@', '£', '$', '¥', 'è', 'é', 'ù', 'ì', 'ò', 'Ç', '\n', 'Ø', 'ø', '\r', 'Å', 'å',
  'Δ', '_', 'Φ', 'Γ', 'Λ', 'Ω', 'Π', 'Ψ', 'Σ', 'Θ', 'Ξ', 'Æ', 'æ', 'ß', 'É',
  ' ', '!', '"', '#', '¤', '%', '&', "'", '(', ')', '*', '+', ',', '-', '.', '/',
  '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ':', ';', '<', '=', '>', '?',
  '¡', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O',
  'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'Ä', 'Ö', 'Ñ', 'Ü', '§',
  '¿', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o',
  'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', 'ä', 'ö', 'ñ', 'ü', 'à'
]);

// Extended GSM-7 characters (these count as 2 characters)
const GSM_7_EXTENDED_CHARS = new Set(['|', '^', '€', '{', '}', '[', ']', '~', '\\']);

// Common emoji ranges for detection
const EMOJI_REGEX = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2300}-\u{23FF}]|[\u{2B50}]|[\u{203C}\u{2049}]|[\u{20E3}]|[\u{00A9}\u{00AE}]/gu;

/**
 * @typedef {Object} SmsCharacterInfo
 * @property {number} charCount
 * @property {number} segmentCount
 * @property {number} maxCharsPerSegment
 * @property {number} maxCharsForSingleSms
 * @property {boolean} isUnicode
 * @property {boolean} hasEmoji
 * @property {number} remainingInSegment
 * @property {string[]} emojiList
 */

/**
 * @typedef {'safe' | 'warning' | 'danger'} SmsCounterStatus
 */

/**
 * Check if the message contains emojis
 */
export function containsEmoji(text) {
  const matches = text?.match(EMOJI_REGEX);
  return {
    hasEmoji: matches !== null && matches?.length > 0,
    emojiList: matches ? [...new Set(matches)] : []
  };
}

/**
 * Check if message requires Unicode encoding (non-GSM-7 characters)
 */
export function requiresUnicode(text) {
  for (const char of text) {
    if (!GSM_7_BASIC_CHARS.has(char) && !GSM_7_EXTENDED_CHARS.has(char)) {
      return true;
    }
  }
  return false;
}

/**
 * Calculate GSM-7 character count (extended chars count as 2)
 */
export function calculateGsm7Length(text) {
  let count = 0;
  for (const char of text) {
    if (GSM_7_EXTENDED_CHARS.has(char)) {
      count += 2;
    } else {
      count += 1;
    }
  }
  return count;
}

/**
 * Get comprehensive SMS character information following Twilio GSM rules
 */
export function getSmsCharacterInfo(text) {
  const emojiInfo = containsEmoji(text);
  const isUnicode = requiresUnicode(text);
  
  // Calculate character count based on encoding
  let charCount;
  if (isUnicode) {
    // Unicode: each character is 1, but we count actual length
    charCount = [...text].length;
  } else {
    // GSM-7: extended chars count as 2
    charCount = calculateGsm7Length(text);
  }
  
  // Set limits based on encoding
  const maxCharsForSingleSms = isUnicode ? 70 : 160;
  const maxCharsPerSegment = isUnicode ? 67 : 153;
  
  // Calculate segment count
  let segmentCount;
  if (charCount === 0) {
    segmentCount = 0;
  } else if (charCount <= maxCharsForSingleSms) {
    segmentCount = 1;
  } else {
    segmentCount = Math.ceil(charCount / maxCharsPerSegment);
  }
  
  // Calculate remaining characters in current segment
  let remainingInSegment;
  if (charCount === 0) {
    remainingInSegment = maxCharsForSingleSms;
  } else if (charCount <= maxCharsForSingleSms) {
    remainingInSegment = maxCharsForSingleSms - charCount;
  } else {
    const usedInLastSegment = charCount % maxCharsPerSegment || maxCharsPerSegment;
    remainingInSegment = maxCharsPerSegment - usedInLastSegment;
  }
  
  return {
    charCount,
    segmentCount,
    maxCharsPerSegment,
    maxCharsForSingleSms,
    isUnicode,
    hasEmoji: emojiInfo.hasEmoji,
    remainingInSegment,
    emojiList: emojiInfo.emojiList
  };
}

/**
 * Get the status color for the SMS counter
 * Green: Safely within 1 SMS
 * Orange: Approaching limit (~85% of single SMS)
 * Red: Exceeds single SMS (multi-segment)
 */
export function getSmsCounterStatus(info) {
  if (info.charCount === 0) return 'safe';
  if (info.segmentCount > 1) return 'danger';
  
  const usagePercent = info.charCount / info.maxCharsForSingleSms;
  if (usagePercent >= 0.85) return 'warning';
  
  return 'safe';
}

/**
 * Check if SMS can be sent based on remaining allowance
 */
export function canSendSms(segmentCount, remainingAllowance) {
  return segmentCount <= remainingAllowance;
}

/**
 * Calculate how many SMS messages would be consumed
 */
export function calculateSmsConsumption(segmentCount, recipientCount) {
  return segmentCount * recipientCount;
}
